import { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  LinearProgress,
  Stack,
  Autocomplete,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import {
  Investors,
  setInvestorsTransactionsDialog,
  setInvestorsTransactionsSearchBody,
} from "../../../../app/slices/investorsSlice";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import dayjs from "dayjs";
import {
  HiOutlineDocumentText,
  HiOutlineFunnel,
  HiOutlineArrowsRightLeft,
} from "react-icons/hi2";
import FormDialog, {
  SectionCard,
  PlainCard,
} from "../../../../components/FormDialog";
import {
  MobileBadge,
  MobileEmpty,
  MobileListItem,
  MobileListShell,
  MobilePagination,
  MobileSummaryCard,
} from "../../../../components/MobileList";

const InvestorsTransactions = () => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const _InvestorTransactions = useSelector(
    (state) => state.investors.investorTransactions
  );
  const _SearchBody = useSelector(
    (state) => state.investors.investorTransactions.searchBody
  );
  const loading = useSelector((state) => state.investors.investors.loading);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [page, setPage] = useState(15);

  const handleClose = () => {
    dispatch(setInvestorsTransactionsDialog(null));
  };

  const type = [
    {
      value: "investor_deposit",
      key: "إيداع",
    },
    { value: "investor_withdraw", key: "سحب" },
  ];

  const columns = [
    {
      field: "number",
      headerName: "رقم المعاملة",
      flex: 1,
    },
    {
      field: "amount",
      headerName: "المبلغ",
      flex: 1,
      renderCell: (params) => params.row.amount.toLocaleString(),
    },
    {
      field: "investor_balance",
      headerName: "رصيد الممول السابق",
      flex: 1,
      renderCell: (params) => params.row.investor_balance.toLocaleString(),
    },
    {
      field: "current_investor_balance",
      headerName: "رصيد الممول الحالي",
      flex: 1,
      renderCell: (params) =>
        params.row.current_investor_balance.toLocaleString(),
    },
    {
      field: "main_balance",
      headerName: "رصيد الخزنة السابق",
      flex: 1,
      renderCell: (params) => params.row.main_balance.toLocaleString(),
    },
    {
      field: "current_main_balance",
      headerName: "رصيد الخزنة الحالي",
      flex: 1,
      renderCell: (params) => params.row.current_main_balance.toLocaleString(),
    },
    {
      field: "by_user",
      headerName: "بواسطة",
      flex: 1,
    },
    {
      field: "type",
      headerName: "النوع",
      flex: 1,
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) => (
        <Stack>
          <Typography variant="body2">
            {dayjs(params.row.created_at).format("YYYY-MM-DD ")}
          </Typography>
          <Typography variant="caption">
            {dayjs(params.row.created_at).format("hh:mm a")}
          </Typography>
        </Stack>
      ),
    },
  ];

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          المجموع الكلي : {_InvestorTransactions.total_amount.toLocaleString()}{" "}
          (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(Investors.getAllInvestorTransactions(_SearchBody));
    }, 500);
    return () => clearTimeout(timeout);
  }, [_SearchBody, dispatch]);

  useEffect(() => {
    dispatch(
      setInvestorsTransactionsSearchBody({
        investor_id: _InvestorTransactions.investor_id,
        type: search ?? "",
        skip: currentPage * page,
        take: page,
      })
    );
  }, [_InvestorTransactions.investor_id, currentPage, dispatch, page, search]);

  return (
    <FormDialog
      open={_InvestorTransactions.dialog}
      onClose={handleClose}
      icon={<HiOutlineDocumentText />}
      title="المعاملات"
      subtitle={`المجموع الكلي : ${(
        _InvestorTransactions.total_amount ?? 0
      ).toLocaleString()} (IQD)`}
      maxWidth="lg"
    >
      <SectionCard icon={<HiOutlineFunnel />} title="الفلاتر">
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={type}
            getOptionLabel={(option) => {
              return option.key;
            }}
            renderInput={(params) => (
              <TextField {...params} label="النوع" size="small" />
            )}
            onChange={(e, newValue) => {
              setSearch(newValue ? newValue.value : null);
              setCurrentPage(0);
            }}
            value={type.filter((x) => x.value === search)[0] ?? null}
          />
        </Grid>
      </SectionCard>

      {smallScreen ? (
        <MobileListShell loading={loading}>
          <MobileSummaryCard
            value={(_InvestorTransactions.total_amount ?? 0).toLocaleString()}
            icon={<HiOutlineDocumentText />}
          />
          {_InvestorTransactions.data?.length > 0 ? (
            <>
              {_InvestorTransactions.data.map((transaction, index) => (
                <MobileListItem
                  key={index}
                  avatar={<HiOutlineArrowsRightLeft />}
                  title={`معاملة #${transaction?.number}`}
                  subtitle={dayjs(transaction.created_at).format(
                    "YYYY-MM-DD · hh:mm a"
                  )}
                  badge={
                    transaction?.type ? (
                      <MobileBadge label={transaction.type} />
                    ) : null
                  }
                  highlight={{
                    label: "المبلغ",
                    value: (transaction?.amount ?? 0).toLocaleString(),
                    unit: "IQD",
                  }}
                  fields={[
                    {
                      label: "رصيد الممول السابق",
                      value: transaction?.investor_balance?.toLocaleString(),
                    },
                    {
                      label: "رصيد الممول الحالي",
                      value:
                        transaction?.current_investor_balance?.toLocaleString(),
                    },
                    {
                      label: "رصيد الخزنة السابق",
                      value: transaction?.main_balance?.toLocaleString(),
                    },
                    {
                      label: "رصيد الخزنة الحالي",
                      value:
                        transaction?.current_main_balance?.toLocaleString(),
                    },
                    {
                      label: "بواسطة",
                      value: transaction?.by_user,
                      span: 12,
                    },
                  ]}
                />
              ))}
              <MobilePagination
                count={_InvestorTransactions.total ?? 0}
                rowsPerPage={page}
                page={currentPage}
                onPageChange={(e, newPage) => setCurrentPage(newPage)}
                rowsPerPageOptions={[15, 25, 50, 100]}
                onRowsPerPageChange={(event) => {
                  setPage(parseInt(event.target.value, 10));
                  setCurrentPage(0);
                }}
              />
            </>
          ) : (
            <MobileEmpty />
          )}
        </MobileListShell>
      ) : (
        <PlainCard>
          <Box sx={{ height: "60vh", width: "100%" }}>
            <DataGrid
              rows={
                _InvestorTransactions.data?.map((x, index) => ({
                  ...x,
                  index: index + 1,
                })) ?? []
              }
              getRowId={(row) => row.number}
              columns={columns}
              pageSize={page}
              page={currentPage}
              rowCount={_InvestorTransactions.total}
              loading={loading}
              rowHeight={50}
              pagination
              paginationMode="server"
              selectionModel={[]}
              onPageChange={(newPage) => {
                setCurrentPage(newPage);
              }}
              rowsPerPageOptions={[15, 25, 50, 100]}
              onPageSizeChange={(newPageSize) => {
                setPage(newPageSize);
              }}
              components={{
                LoadingOverlay: LinearProgress,
                Footer: CustomFooter,
              }}
              sx={{ border: "none" }}
            />
          </Box>
        </PlainCard>
      )}
    </FormDialog>
  );
};

export default InvestorsTransactions;
